/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useCallback, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import {
  Column,
  Row,
  Button,
  DatePicker,
  LabeledInput,
  Textarea,
  ResponsiveOverlay,
} from 'components/Blocks';
import { CheckIcon, XIcon } from 'components/Icons';
import {
  RequiredJustificationAlert,
  isValidJustification,
} from 'components/RequiredJustificationAlert';

import { useMultiStateV2 } from 'hooks';

import { emptyFn } from 'utils/function';

import { JustificationCtx, OpenJustificationModalParams } from './justification.context';

type ModalState = OpenJustificationModalParams & {
  isOpen: boolean;
  loading: boolean;
  justification: string;
  date: Date | null;
  property: string;
};

const initState = {
  isOpen: false,
  justification: '',
  property: '',
  date: null,
  loading: false,
  type: 'normal' as const,
};

export function JustificationModalProvider({ children }: { children: ReactNode }) {
  const { t } = useLang();

  const [state, { dispatch }] = useMultiStateV2(initState as ModalState);

  const closeModal = useCallback(() => {
    dispatch((old) => ({
      ...old,
      ...initState,
      onConfirm: emptyFn,
      cancel: emptyFn,
    }));
  }, [dispatch]);

  const handleCloseModal = (): void => {
    state.cancel?.();

    closeModal();
  };

  const isDateModal = state?.type === 'date';
  const isPropModal = state?.type === 'property';

  const invalidPropertyMessage = isPropModal
    ? !!state.property && state.propertyValidation?.(state.property)
    : false;

  const justificationOk = isValidJustification(state.justification);

  const handleAccept = async (): Promise<void> => {
    if (state.loading || !justificationOk) return;

    const data = state.justification.trim();

    const blocked = [
      isDateModal ? !state.date : false,
      isPropModal ? !state.property.trim() && !invalidPropertyMessage : false,
    ].some(Boolean);

    if (!data || blocked) return;

    const input = isDateModal ? state.date : isPropModal ? state.property.trim() : undefined;

    try {
      dispatch({ loading: true });

      await state.onConfirm?.(data, input as any);

      closeModal();
    } finally {
      dispatch({ loading: false });
    }
  };

  const openJustificationModal = useCallback(
    // because we have fns, we should only directly update when modifying them
    (config: OpenJustificationModalParams): void =>
      dispatch((old) => ({ ...old, ...config, isOpen: true })),
    [dispatch],
  );

  const data = useMemo(
    () => ({
      openJustificationModal,
    }),
    [openJustificationModal],
  );

  const charLimitReached = state?.charLimit
    ? state.charLimit - state.justification.length <= 0
    : false;

  return (
    <JustificationCtx.Provider value={data}>
      <>{children}</>

      <ResponsiveOverlay
        isOpen={state.isOpen}
        close={state.loading ? emptyFn : handleCloseModal}
        title={state?.title ?? t('provide-justification')}
        width="min(80vw, 560px)"
      >
        {typeof state.text === 'string' ? <p className="text-start">{state.text}</p> : state.text}

        {isDateModal && (
          <Row justifyContent="center">
            <DatePicker
              onSelect={(date) => date && dispatch({ date })}
              selected={state.date ?? undefined}
              disablePast
              disabled={state?.shouldDisableDate}
              className="w-full rounded-md "
            />
          </Row>
        )}

        {isPropModal && (
          <LabeledInput
            id="new-value"
            value={state.property}
            onChange={({ target }) => dispatch({ property: target.value })}
            autoComplete="false"
            label={t('new-value')}
            error={!!invalidPropertyMessage && t(invalidPropertyMessage)}
          />
        )}

        <Column gap="1">
          <Textarea
            value={state.justification}
            placeholder={`${t('justification')}...`}
            className="min-h-16"
            onChange={({ target }) => {
              const newValue = target.value.slice(0, state?.charLimit);

              if (!charLimitReached) return dispatch({ justification: newValue });

              if (newValue.length < state.justification.length)
                return dispatch({ justification: newValue });
            }}
          />

          {state?.charLimit && (
            <Row justifyContent="flex-end">
              <p className="w-fil text-sm">{state.charLimit - state.justification.length}</p>
            </Row>
          )}
        </Column>

        <RequiredJustificationAlert text={state.justification} />

        <Row justifyContent="space-between">
          <Button variant="outlined" onClick={handleCloseModal}>
            <XIcon />
            {t('cancel')}
          </Button>

          <Button onClick={handleAccept} loading={state.loading} disabled={!justificationOk}>
            <CheckIcon />
            {t('confirm')}
          </Button>
        </Row>
      </ResponsiveOverlay>
    </JustificationCtx.Provider>
  );
}
